import { IconVariants, MultiSelect } from "@next-platform/client-common-spicy-ui";
import { CharacterFlow, NotificationType, useApp, useSuggestion, useTranslatedTag, useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useMemo, useState } from "react";

import { MAXIMUM_TAG_LIMIT } from "@/constants";
import useToast from "@/hooks/useToast";
import { cn } from "@/utils";
import { getTranslatedTagLabel } from "@/utils/getTagsList";

import { PLACEHOLDER_STYLING, TAG_LIMIT_REACHED_MESSAGE } from "../constants";
import { CharacterFormFields } from "../types";
import getCharacterFormPlaceholders from "./utils/getCharacterFormPlaceholders";

const MAX_SUGGESTIONS = 5;

type CharacterTagsProps = {
  initialValues: Tag[];
  disabled: boolean;
  setSelectedTags: (value: Tag[]) => void;
  characterFields?: {
    name?: string;
    title?: string;
    greeting?: string;
    persona?: string;
    scenario?: string;
    dialogue?: string;
  };
};

type Tag = {
  label: string;
  value: string;
  isSuggested?: boolean;
  confidence?: number;
};

const CharacterTags = ({ initialValues, setSelectedTags, disabled, characterFields }: CharacterTagsProps) => {
  const { t } = useTranslation();
  const { getTranslatedTag } = useTranslatedTag();
  const normalizedInitialValues = useMemo(() => {
    if (!initialValues || !Array.isArray(initialValues)) return [];

    return initialValues.map((tag: Tag | string) => {
      if (typeof tag === "object" && tag !== null && "label" in tag && "value" in tag) {
        return tag;
      }

      if (typeof tag === "string") {
        return { label: tag, value: tag };
      }

      const stringTag = String(tag);
      return { label: stringTag, value: stringTag };
    });
  }, [initialValues]);

  const [selectedOption, setSelectedOption] = useState<Tag[] | null>(null);
  const [suggestedTags, setSuggestedTags] = useState<Tag[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lastFetchedFields, setLastFetchedFields] = useState("");
  const { tagOptions: sortedTags } = useApp();
  const { getSuggestedTags, loadingType, loading } = useSuggestion();
  const { displayToast } = useToast();

  // We get some duplicates, so we create a unique set here
  const uniqueSortedTags = useMemo(() => {
    return Array.from(new Set(sortedTags));
  }, [sortedTags]);

  const isFetchingSuggestions = useMemo(() => loading && loadingType === CharacterFlow.SUGGEST_TAGS, [loading, loadingType]);
  const isTagsLimitReached = selectedOption && selectedOption.length >= MAXIMUM_TAG_LIMIT;

  const handleMultiSelectChange = (values: string[]) => {
    const tagValues = values.map((value: string) => ({ label: value, value }));
    handleChange(tagValues);
  };

  const handleChange = (selectedOption: Tag[]) => {
    if (isTagsLimitReached && selectedOption?.length >= MAXIMUM_TAG_LIMIT) return displayToast({ message: TAG_LIMIT_REACHED_MESSAGE });

    const findTag = (tagObj: Tag) => tagObj.value === "Step-Family";

    const tagExist = normalizedInitialValues.find(findTag);

    const tagExistInSelection = selectedOption.some(findTag);

    if (tagExist && !tagExistInSelection) {
      return setSelectedOption([...selectedOption, tagExist]);
    }

    setSelectedOption(selectedOption);
  };

  // Check if required fields are filled for tag suggestions
  const areRequiredFieldsFilled = useCallback(() => {
    if (!characterFields) {
      return false;
    }
    const { name, title, greeting, persona } = characterFields;
    return name?.trim() && title?.trim() && greeting?.trim() && persona?.trim();
  }, [characterFields]);

  // Create a hash of required fields to detect changes
  const getRequiredFieldsHash = useCallback(() => {
    if (!characterFields) {
      return "";
    }
    const { name, title, greeting, persona } = characterFields;
    return JSON.stringify({
      name: name?.trim(),
      title: title?.trim(),
      greeting: greeting?.trim(),
      persona: persona?.trim(),
    });
  }, [characterFields]);

  // Fetch tag suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!areRequiredFieldsFilled() || !characterFields || isFetchingSuggestions) {
      return;
    }

    const currentFieldsHash = getRequiredFieldsHash();

    // Don't fetch if we already have suggestions for these exact fields
    if (currentFieldsHash === lastFetchedFields) {
      return;
    }

    const { data: response, error } = await getSuggestedTags(characterFields);
    if (error) {
      setSuggestedTags([]);
      displayToast({ message: error, type: NotificationType.error });
      return;
    }
    if (response?.suggested_tags && Array.isArray(response.suggested_tags)) {
      // Convert suggested tags to the format expected
      // Take max suggestions based on MAX_SUGGESTIONS constant
      const suggestions = response.suggested_tags.slice(0, MAX_SUGGESTIONS).map((tag: { label?: string; confidence?: number } | string) => ({
        label: typeof tag === "string" ? tag : tag.label || "",
        value: typeof tag === "string" ? tag : tag.label || "",
        isSuggested: true,
        confidence: typeof tag === "object" ? tag.confidence : undefined,
      }));

      // Filter out suggestions that are already selected to avoid duplicates
      const selectedValues = (selectedOption || normalizedInitialValues || []).map((tag: Tag) => tag.value);
      const filteredSuggestions = suggestions.filter((suggestion: Tag) => !selectedValues.includes(suggestion.value));

      setSuggestedTags(filteredSuggestions);
      setLastFetchedFields(currentFieldsHash);
    } else {
      setSuggestedTags([]);
    }
  }, [
    areRequiredFieldsFilled,
    characterFields,
    getRequiredFieldsHash,
    lastFetchedFields,
    isFetchingSuggestions,
    selectedOption,
    normalizedInitialValues,
  ]);

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);

    // Fetch suggestions when menu opens, but only if we haven't fetched for current fields
    if (areRequiredFieldsFilled()) {
      const currentFieldsHash = getRequiredFieldsHash();
      if (currentFieldsHash !== lastFetchedFields) {
        fetchSuggestions();
      }
    }
  }, [areRequiredFieldsFilled, getRequiredFieldsHash, lastFetchedFields, fetchSuggestions]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleMenuOpenChange = (value: boolean) => {
    if (value) {
      handleMenuOpen();
    } else {
      handleMenuClose();
    }
  };

  useEffect(() => {
    setSelectedTags(selectedOption ?? []);
  }, [selectedOption]);

  // Reset suggestions when required fields change
  useEffect(() => {
    const currentFieldsHash = getRequiredFieldsHash();

    // If fields changed, clear previous suggestions and reset lastFetchedFields
    if (currentFieldsHash !== lastFetchedFields && lastFetchedFields !== "") {
      setSuggestedTags([]);
      setLastFetchedFields("");
    }
  }, [getRequiredFieldsHash, lastFetchedFields]);

  // Filter out suggested tags from regular options to avoid duplicates
  const suggestedTagValues = suggestedTags.map((tag: Tag) => tag.value);
  const filteredSortedTags =
    isMenuOpen && suggestedTags.length > 0 ? uniqueSortedTags.filter((tag: string) => !suggestedTagValues.includes(tag)) : uniqueSortedTags;

  const enabledTagOptions = filteredSortedTags.map((items: string) => ({
    label: items,
    value: items,
  }));

  const areAllSuggestedSelected = suggestedTags.every((suggestedTag) =>
    (selectedOption || normalizedInitialValues).some((tag: Tag) => tag.value === suggestedTag.value),
  );

  // Combine suggested tags and headers with regular options
  const allOptions = [
    {
      key: "header-suggested",
      heading: !isFetchingSuggestions,
      loading: isFetchingSuggestions,
      label: isFetchingSuggestions ? "" : t("common:tags.suggestions.suggested"),
      description: isFetchingSuggestions ? t("common:tags.suggestions.generating") : "",
      icon: IconVariants.WAND_SPARKLES,
      isHidden: !isFetchingSuggestions && (!suggestedTags.length || areAllSuggestedSelected),
    },
    ...suggestedTags.map((tag: Tag) => ({ ...tag, key: `suggested-${tag.value}` })),
    {
      key: "header-all",
      heading: true,
      label: t("common:tags.all"),
      icon: IconVariants.SEARCH,
    },
    ...enabledTagOptions.map((tag: { label: string; value: string }) => ({ ...tag, key: `tag-${tag.value}` })),
  ];

  const selectedRawValues = (selectedOption || normalizedInitialValues).map((tag: Tag) => tag.value);

  const translatedOptions = allOptions.map((option) =>
    "heading" in option
      ? { ...option }
      : {
          ...option,
          label: getTranslatedTagLabel(option.label, getTranslatedTag),
        },
  );

  const getSelectedTagLabel = useCallback((value: string) => getTranslatedTagLabel(value, getTranslatedTag), [getTranslatedTag]);

  return (
    <MultiSelect
      disabled={disabled}
      options={translatedOptions}
      // @ts-ignore to be changed in spice-ui-web
      onChange={handleMultiSelectChange}
      // @ts-ignore to be changed in spice-ui-web
      selectedValues={selectedRawValues}
      // @ts-ignore to be changed in spice-ui-web
      getSelectedLabel={getSelectedTagLabel}
      placeholder={getCharacterFormPlaceholders(t)[CharacterFormFields.TAGS]}
      closeMenuOnSelect={false}
      isMenuOpen={isMenuOpen}
      onOpenChange={handleMenuOpenChange}
      inputProps={{ showMaxLengthLabel: false }}
      maxSelectionLimit={MAXIMUM_TAG_LIMIT}
      classNames={{ dropdownMenu: cn(isTagsLimitReached && "*:opacity-50"), input: { input: cn(PLACEHOLDER_STYLING) } }}
    />
  );
};

export default CharacterTags;
